// packages/ui/src/Drawer.tsx
'use client';

import { ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { isTopDialog, pushDialog, removeDialog } from './dialogStack';
import { currentOpener, focusInitial, restoreFocus, trapTab } from './focus';

export interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  width?: 'md' | 'lg' | 'xl';
  /** True while a save is in flight: the close button is disabled, and Escape and the overlay do nothing. */
  closeDisabled?: boolean;
}

const WIDTH_CLASSES = {
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-3xl',
};

export function Drawer({ isOpen, ...rest }: DrawerProps) {
  // The panel lives in its own component so every open starts from fresh state: the opener is
  // read during that first render, before anything inside can autoFocus and steal it.
  return isOpen ? <DrawerPanel {...rest} /> : null;
}

function DrawerPanel({ onClose, title, children, width = 'lg', closeDisabled = false }: Omit<DrawerProps, 'isOpen'>) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [opener] = useState<HTMLElement | null>(currentOpener);

  // Latest onClose for the document listener, without re-running the mount effect.
  const latest = useRef(onClose);
  const disabledRef = useRef(closeDisabled);
  useEffect(() => {
    latest.current = onClose;
    disabledRef.current = closeDisabled;
  });

  // A layout effect, so a Modal mounting in the same commit (its own push is a passive effect)
  // still ends up above the drawer. Tab is trapped only while this is the top layer.
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const entry = { container: panel };
    pushDialog(entry);
    focusInitial(panel, bodyRef.current);

    const onKeyDown = (e: KeyboardEvent) => {
      if (!isTopDialog(entry)) return;
      if (e.key === 'Escape') {
        // An inner control (combobox, dropdown) that handled Escape wins.
        if (e.defaultPrevented) return;
        e.preventDefault();
        if (!disabledRef.current) latest.current();
      } else {
        trapTab(e, panel);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      removeDialog(entry);
      restoreFocus(opener);
    };
  }, [opener]);

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Overlay */}
      <div className="fixed inset-0 bg-slate-900/50 transition-opacity" onClick={closeDisabled ? undefined : onClose} />

      {/* Panel: slides in from the right */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`relative ml-auto flex flex-col h-full w-full ${WIDTH_CLASSES[width]} bg-white shadow-xl outline-none`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 shrink-0">
          <h2 id={titleId} className="font-semibold text-slate-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={closeDisabled}
            title={closeDisabled ? 'Sedang menyimpan...' : undefined}
            aria-label="Tutup"
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors disabled:text-slate-300 disabled:hover:bg-transparent disabled:cursor-not-allowed"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>

        {/* Scrollable content */}
        <div ref={bodyRef} className="flex-1 overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
}
